/**
 * AppStorePlans — the paywall's purchase block inside the iPad app
 * (ipad-native-shell 10.3). Replaces CheckoutPanel there; Stripe never loads.
 *
 * - No offers (IAP not configured, plugin absent, or Apple has no such products
 *   yet): the no-purchase copy 10.7 shipped, unchanged.
 * - Anyone but a parent or an adult (a kid, a teen): no purchase at all. The
 *   server only accepts their transactions, and a kids app must not put a buy
 *   button in front of a child.
 * - A grown-up's session: one button per plan, priced by Apple, each behind the
 *   Parental_Gate; "Restore purchases"; the auto-renew disclosure Apple requires.
 */
import { useContext, useEffect, useState } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { LegalModal } from "../legal/LegalModal";
import type { LegalDocId } from "../legal/docs";
import { AuthContext } from "../hooks/useAuth";
import { useParentalGate } from "../safety/ParentalGate";
import {
  buyOnAppStore,
  canBuyOnAppStore,
  loadAppStoreOffers,
  priceLine,
  restoreAppStorePurchases,
  type AppStoreOffer,
} from "./appStore";
import { planById } from "./plans";
import type { BillingState } from "./types";

const NO_PURCHASE =
  "Plans aren't available in the iPad app yet. A grown-up can ask us about it at support@worldby.me.";

const NOTE: Record<string, string> = {
  pending: "Waiting for approval. We'll unlock as soon as it goes through.",
  unconfirmed:
    "Your purchase went through, but we couldn't confirm it yet. Tap Restore purchases in a moment.",
  failed: "That didn't go through. Please try again.",
  restored_none: "We didn't find a subscription on this Apple ID.",
  restored_lapsed: "We found this Apple ID's subscription, but it has ended. Pick a plan to start it again.",
};

export interface AppStorePlansProps {
  /** A purchase unlocked the family. */
  onUnlocked: (state: BillingState) => void;
  /** Restore found a live subscription. Not a new purchase: no /subscribed landing. */
  onRestored?: (state: BillingState) => void;
}

const UNLOCKS: ReadonlySet<string> = new Set(["active", "trialing"]);

export function AppStorePlans({ onUnlocked, onRestored }: AppStorePlansProps): JSX.Element {
  // No auth context fails safe to the kid path: no purchase.
  const auth = useContext(AuthContext);
  const role = auth?.role;
  const tenantId = auth?.tenantId;
  const gate = useParentalGate();
  const [offers, setOffers] = useState<AppStoreOffer[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // Terms / Privacy open in the in-app legal sheet: a plain link would navigate
  // the whole web view away from the paywall.
  const [legalDoc, setLegalDoc] = useState<LegalDocId | null>(null);

  useEffect(() => {
    let live = true;
    void loadAppStoreOffers().then((o) => {
      if (live) setOffers(o);
    });
    return () => {
      live = false;
    };
  }, []);

  const muted = { fontSize: 14, lineHeight: 1.5, color: ChromeTokens.color.inkMuted } as const;
  const wrap = { marginTop: ChromeTokens.spacing.lg } as const;

  if (offers === null) return <div data-testid="appstore-loading" style={wrap} />;
  if (offers.length === 0) {
    return (
      <div data-testid="unlock-native-no-checkout" style={{ ...wrap, ...muted }}>
        {NO_PURCHASE}
      </div>
    );
  }
  if (!auth || !canBuyOnAppStore(role) || !tenantId) {
    return (
      <div data-testid="appstore-kid" style={{ ...wrap, ...muted }}>
        A grown-up can unlock this. Ask them to sign in on this device and come back here.
      </div>
    );
  }

  const buy = (o: AppStoreOffer) =>
    gate.run("purchase", () => {
      setBusy(true);
      setNote(null);
      void buyOnAppStore(o.id, tenantId)
        .then((r) => {
          if (r.kind === "unlocked") onUnlocked(r.state);
          else if (r.kind !== "cancelled") setNote(NOTE[r.kind]);
        })
        .finally(() => setBusy(false));
    });

  const restore = () =>
    gate.run("purchase", () => {
      setBusy(true);
      setNote(null);
      void restoreAppStorePurchases()
        .then(({ restored, state }) => {
          if (state && UNLOCKS.has(state.status)) (onRestored ?? onUnlocked)(state);
          else if (restored === 0) setNote(NOTE.restored_none);
          else setNote(NOTE.restored_lapsed);
        })
        .finally(() => setBusy(false));
    });

  // The billing link style (UnlockModal, ConsentGateModal), not the browser's blue.
  const link = { color: ChromeTokens.color.clay, fontWeight: 700, textDecoration: "underline" } as const;

  const button = {
    minHeight: 52,
    width: "100%",
    border: "none",
    borderRadius: ChromeTokens.radius.md,
    background: ChromeTokens.color.sun,
    color: ChromeTokens.color.ink,
    fontFamily: ChromeTokens.font.ui,
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  } as const;

  return (
    <div data-testid="appstore-plans" style={{ ...wrap, display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm }}>
      {offers.map((o) => (
        <button key={o.id} type="button" disabled={busy} onClick={() => buy(o)} style={button}>
          <span>{planById(o.planId).name}</span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{priceLine(o)}</span>
        </button>
      ))}
      {note && (
        <div role="status" style={{ ...muted, fontSize: 13 }}>
          {note}
        </div>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={restore}
        style={{ background: "none", border: "none", cursor: "pointer", color: ChromeTokens.color.inkMuted, fontFamily: ChromeTokens.font.ui, fontSize: 13, fontWeight: 700, minHeight: 44 }}
      >
        Restore purchases
      </button>
      <div style={{ ...muted, fontSize: 12 }}>
        Payment is charged to your Apple ID. The subscription renews automatically unless you cancel
        it at least 24 hours before the period ends, in Settings ▸ your name ▸ Subscriptions.{" "}
        <a href="/terms" style={link} onClick={(e) => { e.preventDefault(); setLegalDoc("terms"); }}>Terms</a>
        {" · "}
        <a href="/privacy" style={link} onClick={(e) => { e.preventDefault(); setLegalDoc("privacy"); }}>Privacy</a>
      </div>
      {gate.Gate && <gate.Gate />}
      <LegalModal open={legalDoc !== null} doc={legalDoc ?? "terms"} onClose={() => setLegalDoc(null)} />
    </div>
  );
}
